'use client';

import { useState, useEffect } from 'react';
import axios from 'axios';
import Link from 'next/link';
import { isLoggedIn } from '@/app/funcs/api';
import { toast } from 'react-toastify';
import './shorten.css';

export default function ShortenComponent({ theme }) {
    const baseURL = process.env.NEXT_PUBLIC_BASE_URL;
    const baseShortURLLINK = process.env.NEXT_PUBLIC_BASE_SHORT_URL_LINK;
    const baseShortURLLNK = process.env.NEXT_PUBLIC_BASE_SHORT_URL_LNK;
    const baseShortURLS = process.env.NEXT_PUBLIC_BASE_SHORT_URL_S;
    const baseShortURLSHORT = process.env.NEXT_PUBLIC_BASE_SHORT_URL_SHORT;

    const [token, setToken] = useState();
    const [isClientLoggedIn, setIsClientLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [url, setUrl] = useState('');
    const [title, setTitle] = useState('');
    const [shortUrl, setShortUrl] = useState('');
    const [shortCode, setShortCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState({ url: false, code: false });
    const [currentTheme, setCurrentTheme] = useState('light');

    useEffect(() => {
        const loggedIn = isLoggedIn();
        setToken(loggedIn ? loggedIn : '');
        setIsClientLoggedIn(!!loggedIn);

        // Get user data from localStorage
        if (typeof window !== 'undefined') {
            const userData = localStorage.getItem('user');
            if (userData) {
                try {
                    const parsedUser = JSON.parse(userData);
                    setUser(parsedUser);
                } catch (error) {
                    console.error('Error parsing user data:', error);
                }
            }
        }

        // Theme detection
        const detectTheme = () => {
            if (typeof window !== 'undefined') {
                const savedTheme = localStorage.getItem('theme');
                const bodyHasDarkClass = document.body.classList.contains('dark');
                const theme = savedTheme || (bodyHasDarkClass ? 'dark' : 'light');
                setCurrentTheme(theme);
            }
        };

        detectTheme();

        // Listen for theme changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'class'
                ) {
                    detectTheme();
                }
            });
        });

        if (typeof window !== 'undefined' && document.body) {
            observer.observe(document.body, { attributes: true });
        }

        return () => observer.disconnect();
    }, []);

    // Get user's username/slug for branded links
    const getUserIdentifier = () => {
        if (!user) {
            return null;
        }

        // Try different possible username fields in order of preference
        const identifier =
            user.slug ||
            user.user_login ||
            user.username ||
            user.user_nicename ||
            user.name?.toLowerCase().replace(/\s+/g, '');

        return identifier;
    };

    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        setShortUrl('');
        setShortCode('');
        setError(null);
        setCopied({ url: false, code: false });

        try {
            let headers = {};
            if (isClientLoggedIn) {
                headers = {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                };
            } else {
                headers = {
                    'Content-Type': 'application/json',
                };
            }

            const response = await axios.post(
                `${baseURL}/api/shorturl/v1/create`,
                {
                    url: url,
                    title: title ? title : 'لا يوجد عنوان',
                },
                {
                    headers: headers,
                }
            );
            setShortUrl(`${baseShortURLLNK}${response.data.short_code}`);
            setShortCode(response.data.short_code);
        } catch (err) {
            setError('خطأ أثناء محاولة اختصار الرابط ، فضلاً حاول مرة أخرى');
        } finally {
            setLoading(false);
        }
    }

    const copyToClipboard = (text, type) => {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                setCopied((prev) => ({ ...prev, [type]: true }));
                // Show success toast notification
                toast.success('تم نسخ الرابط بنجاح 🎉', {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: currentTheme === 'dark' ? 'dark' : 'light',
                });
                setTimeout(
                    () => setCopied((prev) => ({ ...prev, [type]: false })),
                    2000
                );
            })
            .catch((err) => {
                // Show error toast if copying fails
                toast.error('فشل في نسخ الرابط، حاول مرة أخرى', {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: currentTheme === 'dark' ? 'dark' : 'light',
                });
            });
    };

    return (
        <div
            className={`row mt-3 justify-content-center ${currentTheme === 'dark' ? 'theme-dark' : 'theme-light'}`}
        >
            <div className="card p-5 shadow-md rounded-4 col-12 col-md-8">
                {/* Header Section */}
                <div className="shorten-header text-center">
                    <div className="link-icon">
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <h1 className="shorten-title">أداة اختصار الروابط</h1>
                    <p className="shorten-description">
                        شارك روابط أعمالك من فيجما بسهولة وأناقة مع رابط قصير ومخصص
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="shorten-form">
                    {isClientLoggedIn && (
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="اختياري: كتابة وصف بسيط للرابط"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="form-input"
                            />
                        </div>
                    )}

                    <div className="form-group">
                        <input
                            type="url"
                            required
                            placeholder="الصق الرابط المراد اختصاره هنا...."
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            className="form-input"
                            dir="rtl"
                        />
                    </div>

                    {!isClientLoggedIn && (
                        <div className="login-notice">
                            <small>
                                <b>لا يمكنك كتابة وصف مختصر للرابط بدون تسجيل الدخول</b>
                            </small>
                        </div>
                    )}

                    <div className="form-actions">
                        <button type="submit" disabled={loading} className="shorten-button">
                            {loading ? 'جار اختصار الرابط ...' : 'اختصار الرابط'}
                        </button>
                        {!isClientLoggedIn && (
                            <Link href="/login" className="login-button">
                                تسجيل الدخول
                            </Link>
                        )}
                    </div>
                </form>

                {/* Results Section */}
                {shortUrl && (
                    <div className="results-section">
                        <div className="result-item">
                            <p className="result-label">✅ الرابط المختصر:</p>
                            <div className="result-content">
                                <a
                                    href={shortUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="result-link"
                                >
                                    {shortUrl}
                                </a>
                                <button
                                    onClick={() => copyToClipboard(shortUrl, 'url')}
                                    className="copy-button"
                                >
                                    <i className="bi bi-clipboard me-2"></i>
                                    نسخ االرابط السريع
                                </button>
                            </div>
                        </div>

                        <div className="alternative-links">
                            <p className="result-label">خيارات أخرى:</p>

                            {/* Branded link for logged-in users */}
                            {isClientLoggedIn && user && getUserIdentifier() && (
                                <>
                                    <div className="alternative-link">
                                        <a
                                            href={`https://figmawya.com/@${getUserIdentifier()}/${shortCode}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="result-link branded-link"
                                            onClick={() =>
                                                copyToClipboard(
                                                    `https://figmawya.com/@${getUserIdentifier()}/${shortCode}`,
                                                    'url'
                                                )
                                            }
                                        >
                                            https://figmawya.com/@{getUserIdentifier()}/{shortCode}
                                        </a>
                                        <span
                                            className="branded-badge"
                                            onClick={() =>
                                                copyToClipboard(
                                                    `https://figmawya.com/@${getUserIdentifier()}/${shortCode}`,
                                                    'url'
                                                )
                                            }
                                        >
                                            <i className="bi bi-clipboard me-2"></i> رابط مخصص
                                        </span>
                                    </div>

                                    <div className="alternative-link">
                                        <a
                                            href={`https://figmawya.com/@${getUserIdentifier()}/lnk/${shortCode}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="result-link branded-link"
                                        >
                                            https://figmawya.com/@{getUserIdentifier()}/lnk/
                                            {shortCode}
                                        </a>
                                        <span
                                            className="branded-badge"
                                            onClick={() =>
                                                copyToClipboard(
                                                    `https://figmawya.com/@${getUserIdentifier()}/lnk/${shortCode}`,
                                                    'url'
                                                )
                                            }
                                        >
                                            <i className="bi bi-clipboard me-2"></i> رابط مخصص مع
                                            تعريف بك
                                        </span>
                                    </div>
                                </>
                            )}

                            {/* <div className="alternative-link">
                                <a
                                    href={baseShortURLLINK + shortCode}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="result-link"
                                >
                                    {baseShortURLLINK + shortCode}
                                </a>
                            </div>
                            <div className="alternative-link">
                                <a
                                    href={baseShortURLS + shortCode}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="result-link"
                                >
                                    {baseShortURLS + shortCode}
                                </a>
                            </div>
                            <div className="alternative-link">
                                <a
                                    href={baseShortURLSHORT + shortCode}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="result-link"
                                >
                                    {baseShortURLSHORT + shortCode}
                                </a>
                            </div> */}
                        </div>
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}
            </div>
        </div>
    );
}
